import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router'

import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import { getStudentCourse } from '@/services/student_courses.service'

import CourseComplete from './CourseComplete'
import CourseModal from './CourseModal'
import CourseOverview from './CourseOverview'

function StudentCourse() {
  const { courseId, entityId } = useParams()

  const queryClient = useQueryClient()
  const [courseModalOpen, setCourseModalOpen] = useState(false)
  const [initialCourseContent, setInitialCourseContent] = useState(null)

  const { data: courseRecord, isPending } = useQuery({
    queryKey: ['accounts', 'students', 'courses', courseId, entityId],
    queryFn: () => getStudentCourse(entityId, courseId),
  })

  const closeModal = useCallback(() => {
    setCourseModalOpen(false)
    queryClient.invalidateQueries({ queryKey: ['accounts', 'students', 'courses'] })
  }, [queryClient])

  const course = courseRecord?.course
  const progress = courseRecord?.status
  // The certificate is the overall program; `course` is the specific module
  // within it. Surface both so students don't read the module name as the
  // program name (and vice versa).
  const certificate = courseRecord?.studentCertificate?.certificate

  const openToContent = (contentId) => {
    setInitialCourseContent(`content-${contentId}`)
    setCourseModalOpen(true)
  }

  if (isPending) {
    return (
      <Layout>
        <div className="mx-auto mt-4 flex w-full max-w-3xl flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </Layout>
    )
  }
  return (
    <Layout
      breadcrumbs={[
        { title: 'Courses', pathname: `/accounts/${entityId}/students/courses/` },
        ...(certificate?.name ? [certificate.name] : []),
        course.name,
      ]}
    >
      {['Not Started', 'In Progress'].includes(progress) && (
        <CourseOverview
          courseRecord={courseRecord}
          showCourseModal={() => setCourseModalOpen(true)}
          progress={progress}
        />
      )}
      {progress === 'Completed' && (
        <CourseComplete
          courseRecord={courseRecord}
          openToContent={openToContent}
          entityId={entityId}
        />
      )}
      {courseModalOpen && (
        <CourseModal
          courseRecord={courseRecord}
          closeModal={closeModal}
          initialCourseContent={initialCourseContent}
          entityId={entityId}
        />
      )}
    </Layout>
  )
}

export default StudentCourse
